import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  fetchVisitReportCandidates,
  linkPlannedVisitReport,
  PlannedVisitReportCandidate,
} from '../../api/reportsApi';
import { ReportState } from '../../types/Rows';

// Raport założony poza wizytą planowaną (np. z listy raportów) nie był z nią powiązany,
// więc kalendarz pokazywał „Odbyta, brak raportu”. Tu można go wskazać ręcznie.
type Props = {
  visitId: string;
  getReportStateLabel: (state?: ReportState) => string;
  onLinked: () => void;
};

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : '–');

const errorMessage = (error: unknown) => {
  const data = (error as any)?.response?.data;
  return typeof data === 'string' && data ? data : null;
};

const LinkReportPanel: React.FC<Props> = ({ visitId, getReportStateLabel, onLinked }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { data: candidates, isLoading } = useQuery<PlannedVisitReportCandidate[]>(
    ['visitReportCandidates', visitId],
    () => fetchVisitReportCandidates(visitId),
    { enabled: open }
  );

  const { mutate: link, isLoading: isLinking, error } = useMutation(
    (reportId: string) => linkPlannedVisitReport(visitId, reportId),
    { onSuccess: onLinked }
  );

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-[#F4A832] hover:text-[#e09620] font-medium cursor-pointer text-sm"
      >
        {t('linkExistingReport')}
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)]">{t('linkExistingReport')}</h4>
        <button
          onClick={() => setOpen(false)}
          className="text-sm text-[var(--sc-muted)] hover:text-[var(--sc-text)] cursor-pointer"
        >
          {t('cancel')}
        </button>
      </div>
      <p className="text-xs text-[var(--sc-muted)]">{t('linkExistingReportHint')}</p>

      {isLoading && <p className="text-sm text-[var(--sc-muted)]">{t('loading')}</p>}
      {candidates && candidates.length === 0 && (
        <p className="text-sm text-[var(--sc-muted)]">{t('noReportsInResort')}</p>
      )}
      {!!error && <p className="text-sm text-red-600">{errorMessage(error) || t('linkReportError')}</p>}

      {candidates && candidates.length > 0 && (
        <ul className="divide-y divide-[var(--sc-border)] border border-[var(--sc-border)] rounded-lg max-h-80 overflow-y-auto">
          {candidates.map((c) => {
            const linkedElsewhere = !!c.linkedVisitId;
            return (
              <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                <div className="min-w-0 space-y-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[var(--sc-text)]">{c.name}</span>
                    {c.suggested && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#FFF8ED] text-[#C47A0A] border border-[#F4A832]/30">
                        {t('suggestedReport')}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--sc-muted)]">
                    {[c.templateName, `${t('visitDate')}: ${formatDate(c.visitDate)}`, getReportStateLabel(c.state)]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  {linkedElsewhere && (
                    <div className="text-xs text-[var(--sc-muted)]">
                      {t('reportLinkedToOtherVisit')}: {c.linkedVisitName || '–'}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => link(c.id)}
                  disabled={linkedElsewhere || isLinking}
                  className="shrink-0 px-3 py-1 rounded-full text-xs font-medium bg-[#F4A832] text-[#0F0F0F] hover:bg-[#e09620] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t('linkReport')}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LinkReportPanel;
